import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { cn, formatNumber } from '@/lib/utils'
import type { ConnectorMetrics } from '../types/topology'

interface Props {
  metrics: ConnectorMetrics
  compact?: boolean
}

const stateColors: Record<string, string> = {
  RUNNING: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
  PAUSED: 'bg-yellow-100 text-yellow-700',
  UNASSIGNED: 'bg-orange-100 text-orange-700',
  UNKNOWN: 'bg-gray-100 text-gray-500',
}

function formatEta(sec: number | null): string {
  if (sec === null) return '—'
  if (sec === 0) return 'caught up'
  if (sec < 60) return `${sec}s`
  if (sec < 3600) return `${Math.round(sec / 60)}m`
  return `${(sec / 3600).toFixed(1)}h`
}

function formatRate(ratePerMin: number): string {
  if (ratePerMin < 0) return '—'
  if (ratePerMin === 0) return '0/min'
  if (ratePerMin < 1000) return `${Math.round(ratePerMin)}/min`
  return `${(ratePerMin / 1000).toFixed(1)}k/min`
}

export function ConnectorStatusCard({ metrics, compact = false }: Props) {
  const [traceOpen, setTraceOpen] = useState(false)
  const isFailed = metrics.state === 'FAILED' || metrics.runningTasks < metrics.totalTasks
  const stateColor = stateColors[metrics.state] ?? stateColors.UNKNOWN

  return (
    <div
      className={cn(
        'bg-white border rounded-lg p-4',
        isFailed ? 'border-red-200' : 'border-gray-200'
      )}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0 flex-1">
          <p className={cn('font-medium text-gray-900', compact ? 'text-xs' : 'text-sm')}>
            {metrics.category}
          </p>
          <p className="text-[11px] text-gray-400 font-mono mt-0.5 truncate">{metrics.name}</p>
        </div>
        <span
          className={cn(
            'inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium shrink-0 ml-2',
            stateColor
          )}
        >
          {metrics.state}
        </span>
      </div>

      <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
        <span>
          Tasks:{' '}
          <span className={cn('font-medium', isFailed ? 'text-red-600' : 'text-gray-800')}>
            {metrics.runningTasks}/{metrics.totalTasks}
          </span>
        </span>
        {metrics.totalTasks > 0 && (
          <>
            <span>lag: <span className="font-medium text-gray-800">{formatNumber(metrics.currentLag)}</span></span>
            <span>{formatRate(metrics.throughputPerMin)}</span>
            <span>ETA {formatEta(metrics.etaSeconds)}</span>
          </>
        )}
      </div>

      {metrics.errorTrace && (
        <div>
          <button
            onClick={() => setTraceOpen(v => !v)}
            className="flex items-center gap-1 text-[11px] text-red-500 hover:text-red-700"
          >
            {traceOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            error trace
          </button>
          {traceOpen && (
            <pre className="mt-1.5 text-[10px] text-red-700 bg-red-50 rounded p-2 overflow-x-auto max-h-32 whitespace-pre-wrap break-all">
              {metrics.errorTrace}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}
